// pkg72a — green→navy conversion: decorative dark-green surfaces (#1a3a2a/#2a5a4a — banner gradients, featured borders, buttons, toggles, tabs) become NAVY_DEEP #0F1838 → NAVY #1E2761; SEMANTIC greens kept (status/success badges, category chip triads, ✓ text); selected-module card converts as a pair (navy border + pale-navy #F0F2F8 tint) — delivered 2026-07-12
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// pkg67 — owner/picker split: the platform owner keeps the full catalog management view (now with a practice-area badge); every other firm gets the module CHOICE view — an "In use / Not in use" toggle per available module (firm_enabled_modules) with a Recommended badge on built-ins. Disabling hides a module from client portals without deleting saved data (D-67l) — delivered 2026-07-12
// pkg63 — jurisdiction badge on catalog rows (amber "TN only" etc.); non-platform firms never receive foreign-state modules from the API, so the badge is effectively the platform owner's map of what's state-gated — delivered 2026-07-10
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';

const TYPE_LABELS = {
  builtin:      { label: 'Built-in',    color: '#1a3a2a', bg: '#f0faf5' },
  content_pdf:  { label: 'PDF',         color: '#2f5496', bg: '#f0f4ff' },
  content_html: { label: 'Rich Text',   color: '#5b3a8c', bg: '#f5f0fa' },
};

// pkg63 — a module tagged to a state (modules.jurisdiction) shows an amber
// badge; universal modules (jurisdiction NULL) show nothing.
function JurisdictionBadge({ jurisdiction }) {
  if (!jurisdiction) return null;
  return (
    <span style={{
      fontSize: 11, fontFamily: 'Arial, sans-serif',
      padding: '2px 8px', borderRadius: 3,
      background: '#fdf6e3', color: '#8a6d1a',
      border: '1px solid #8a6d1a33', fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {jurisdiction} only
    </span>
  );
}

// pkg67 — a module tagged to a practice area (modules.practice_area) shows
// the area key in a badge; universal modules (practice_area NULL) show
// nothing. Mostly the platform owner's map of what's area-gated — other
// firms only receive modules matching their own areas anyway.
function PracticeAreaBadge({ practiceArea }) {
  if (!practiceArea) return null;
  const label = String(practiceArea).replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
  return (
    <span style={{
      fontSize: 11, fontFamily: 'Arial, sans-serif',
      padding: '2px 8px', borderRadius: 3,
      background: '#f0f4ff', color: '#2f5496',
      border: '1px solid #2f549633', fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

// pkg67 — the Recommended badge on the non-owner picker view: built-in
// modules are the core client workflow set (and anything a non-owner firm
// can see already passed its jurisdiction + practice-area availability, so
// "built-in" is the honest recommendation line; platform content-library
// modules are available but not pushed).
function RecommendedBadge() {
  return (
    <span style={{
      fontSize: 11, fontFamily: 'Arial, sans-serif',
      padding: '2px 8px', borderRadius: 10,
      background: '#f0faf5', color: '#1a7a5e',
      border: '1px solid #1a7a5e33', fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      Recommended
    </span>
  );
}

function TypeBadge({ type }) {
  const meta = TYPE_LABELS[type] || TYPE_LABELS.builtin;
  return (
    <span style={{
      fontSize: 11, fontFamily: 'Arial, sans-serif',
      padding: '2px 8px', borderRadius: 3,
      background: meta.bg, color: meta.color,
      border: `1px solid ${meta.color}33`, fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {meta.label}
    </span>
  );
}

function ActiveToggle({ m, busy, onToggle }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
      <button
        onClick={onToggle}
        disabled={busy}
        aria-label={m.is_active ? 'Deactivate' : 'Activate'}
        style={{
          width: 48, height: 26, borderRadius: 13,
          background: m.is_active ? '#0F1838' : '#ddd',
          border: 'none', cursor: busy ? 'default' : 'pointer',
          position: 'relative', transition: 'background 0.2s',
        }}>
        <span style={{
          position: 'absolute', top: 3,
          left: m.is_active ? 25 : 3,
          width: 20, height: 20, borderRadius: '50%',
          background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </button>
      <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 10, color: m.is_active ? '#0F1838' : '#aaa', fontWeight: 600, marginTop: 2 }}>
        {m.is_active ? 'Active' : 'Hidden'}
      </div>
    </div>
  );
}

// pkg67 — the non-owner firm's choice toggle (writes firm_enabled_modules
// via POST/DELETE /api/modules/admin/catalog/:id/firm-enabled). Same visual
// language as ActiveToggle so the two views feel like one page.
function FirmEnabledToggle({ m, busy, onToggle }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
      <button
        onClick={onToggle}
        disabled={busy}
        aria-label={m.firm_enabled ? 'Stop using this module' : 'Use this module'}
        style={{
          width: 48, height: 26, borderRadius: 13,
          background: m.firm_enabled ? '#0F1838' : '#ddd',
          border: 'none', cursor: busy ? 'default' : 'pointer',
          position: 'relative', transition: 'background 0.2s',
        }}>
        <span style={{
          position: 'absolute', top: 3,
          left: m.firm_enabled ? 25 : 3,
          width: 20, height: 20, borderRadius: '50%',
          background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </button>
      <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 10, color: m.firm_enabled ? '#0F1838' : '#aaa', fontWeight: 600, marginTop: 2 }}>
        {m.firm_enabled ? 'In use' : 'Not in use'}
      </div>
    </div>
  );
}

export default function AdminModulesPage() {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [busy,    setBusy]    = useState('');

  // pkg67 — null = not yet known (render neither view's controls until the
  // meta call answers, matching the "don't flash the wrong menu" pattern).
  const [platformOwner, setPlatformOwner] = useState(null);

  const [creating, setCreating] = useState(false);
  const [newType,  setNewType]  = useState('content_html');
  const [newLabel, setNewLabel] = useState('');
  const [newDesc,  setNewDesc]  = useState('');

  function loadModules() {
    setLoading(true);
    Promise.all([
      adminApi.listModules(),
      adminApi.getModulesCatalogMeta().catch(() => ({ platform_owner: false })), // pkg67
    ])
      .then(([data, meta]) => {
        setModules(data);
        setPlatformOwner(!!(meta && meta.platform_owner));
        setLoading(false);
      })
      .catch(err  => { setError(err.message); setLoading(false); });
  }

  useEffect(loadModules, []);

  async function toggleActive(m) {
    setBusy(m.id); setError('');
    try {
      const updated = m.is_active
        ? await adminApi.deactivateModule(m.id)
        : await adminApi.activateModule(m.id);
      setModules(prev => prev.map(x => x.id === m.id ? { ...x, ...updated } : x));
    } catch (err) { setError(err.message); }
    finally { setBusy(''); }
  }

  // pkg67 — the non-owner firm's own choice. Optimistic-free: flip only on
  // the server's answer so the toggle never lies about saved state.
  async function toggleFirmEnabled(m) {
    setBusy(m.id); setError('');
    try {
      const r = m.firm_enabled
        ? await adminApi.disableFirmModule(m.id)
        : await adminApi.enableFirmModule(m.id);
      setModules(prev => prev.map(x => x.id === m.id ? { ...x, firm_enabled: !!r.firm_enabled } : x));
    } catch (err) { setError(err.message); }
    finally { setBusy(''); }
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!newLabel.trim()) return;
    setError('');
    try {
      const created = await adminApi.createModule({
        module_type: newType,
        label: newLabel.trim(),
        description: newDesc.trim() || null,
      });
      setCreating(false);
      setNewLabel(''); setNewDesc(''); setNewType('content_html');
      navigate(`/admin/dashboard/modules/${created.id}`);
    } catch (err) { setError(err.message); }
  }

  async function moveUp(idx) {
    if (idx === 0) return;
    const list = [...modules];
    [list[idx - 1], list[idx]] = [list[idx], list[idx - 1]];
    const items = list.map((m, i) => ({ id: m.id, sort_order: (i + 1) * 10 }));
    setModules(list.map((m, i) => ({ ...m, sort_order: items[i].sort_order })));
    try { await adminApi.reorderModules(items); }
    catch (err) { setError(err.message); loadModules(); }
  }

  async function moveDown(idx) {
    if (idx >= modules.length - 1) return;
    const list = [...modules];
    [list[idx + 1], list[idx]] = [list[idx], list[idx + 1]];
    const items = list.map((m, i) => ({ id: m.id, sort_order: (i + 1) * 10 }));
    setModules(list.map((m, i) => ({ ...m, sort_order: items[i].sort_order })));
    try { await adminApi.reorderModules(items); }
    catch (err) { setError(err.message); loadModules(); }
  }

  const thStyle = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif',
    textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600, background: '#0F1838' };
  const tdStyle = { padding: '12px', fontSize: 13, fontFamily: 'Arial, sans-serif',
    borderBottom: '1px solid #eee', verticalAlign: 'middle' };

  const isOwner = platformOwner === true;
  const isPicker = platformOwner === false; // pkg67 — the firm-choice view

  return (
    <div>
      <h1 className="page-title">Modules</h1>
      {isPicker ? (
        <p className="page-subtitle">
          Choose which modules your firm uses. Modules in use can be assigned to clients;
          turning one off hides it from client portals without deleting any saved data —
          turn it back on and everything is exactly as it was.
        </p>
      ) : (
        <p className="page-subtitle">
          Every section a client can see is a module. Built-in modules ship with the portal and can't be deleted (but can be deactivated).
          Content modules are uploaded PDFs or rich-text pages you create here.
        </p>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      {isOwner && !creating && (
        <button className="btn btn-primary" style={{ marginBottom: 16 }} onClick={() => setCreating(true)}>
          + New Content Module
        </button>
      )}

      {isOwner && creating && (
        <>
          <div className="section-head" style={{ marginTop: 0 }}>New Content Module</div>
          <div className="card" style={{ background: '#F5F1E8' }}>
            <form onSubmit={handleCreate}>
              <div className="form-row">
                <div className="form-group" style={{ flex: '0 0 200px' }}>
                  <label>Type</label>
                  <select value={newType} onChange={e => setNewType(e.target.value)}>
                    <option value="content_html">Rich Text (WYSIWYG)</option>
                    <option value="content_pdf">PDF Upload</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Label (shown in sidebar &amp; dashboard)</label>
                  <input type="text" value={newLabel} onChange={e => setNewLabel(e.target.value)}
                    placeholder="e.g. Parenting Class Information" required autoFocus />
                </div>
              </div>
              <div className="form-group">
                <label>Description <span style={{ fontWeight: 400, color: '#888' }}>(optional — appears on the dashboard card)</span></label>
                <input type="text" value={newDesc} onChange={e => setNewDesc(e.target.value)}
                  placeholder="e.g. Information about the court-required parenting class." />
              </div>
              <div className="btn-row">
                <button type="button" className="btn" onClick={() => {
                  setCreating(false); setNewLabel(''); setNewDesc('');
                }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={!newLabel.trim()}>
                  Create &amp; Edit
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {loading || platformOwner === null ? (
        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>Loading...</div>
      ) : modules.length === 0 ? (
        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>No modules yet.</div>
      ) : isPicker ? (
        <>
          {/* ── pkg67: firm-choice view (non-platform firms) ─────────────── */}
          {/* Desktop table */}
          <div className="desktop-only">
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Module</th>
                      <th style={{ ...thStyle, width: 110 }}>Type</th>
                      <th style={{ ...thStyle, width: 100, textAlign: 'center' }}>Clients</th>
                      <th style={{ ...thStyle, width: 130, textAlign: 'center' }}>In Use</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map((m, i) => (
                      <tr key={m.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 500, fontSize: 14 }}>{m.label}</span>
                            {m.module_type === 'builtin' && <RecommendedBadge />}
                          </div>
                          {m.description && (
                            <div style={{ color: '#888', fontSize: 11, marginTop: 2 }}>{m.description}</div>
                          )}
                        </td>
                        <td style={tdStyle}>
                          <TypeBadge type={m.module_type} />
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center', color: '#555' }}>
                          {m.active_client_count ?? 0}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <FirmEnabledToggle m={m} busy={busy === m.id} onToggle={() => toggleFirmEnabled(m)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Mobile card list */}
          <div className="mobile-only">
            {modules.map((m) => (
              <div key={m.id} className="card" style={{ padding: 14, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 14, fontWeight: 500 }}>{m.label}</span>
                      <TypeBadge type={m.module_type} />
                      {m.module_type === 'builtin' && <RecommendedBadge />}
                    </div>
                    {m.description && (
                      <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888', marginTop: 2 }}>{m.description}</div>
                    )}
                    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888', marginTop: 8 }}>
                      {m.active_client_count ?? 0} active client{m.active_client_count === 1 ? '' : 's'}
                    </div>
                  </div>
                  <FirmEnabledToggle m={m} busy={busy === m.id} onToggle={() => toggleFirmEnabled(m)} />
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* ── Platform-owner view (unchanged from pkg63, + practice-area badge) ── */}
          {/* Desktop table */}
          <div className="desktop-only">
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: 80 }}>Order</th>
                      <th style={thStyle}>Module</th>
                      <th style={{ ...thStyle, width: 110 }}>Type</th>
                      <th style={{ ...thStyle, width: 100, textAlign: 'center' }}>Clients</th>
                      <th style={{ ...thStyle, width: 130, textAlign: 'center' }}>Active</th>
                      <th style={{ ...thStyle, width: 100, textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map((m, i) => (
                      <tr key={m.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-sm" style={{ padding: '2px 8px' }}
                              disabled={i === 0} onClick={() => moveUp(i)} title="Move up">↑</button>
                            <button className="btn btn-sm" style={{ padding: '2px 8px' }}
                              disabled={i === modules.length - 1} onClick={() => moveDown(i)} title="Move down">↓</button>
                          </div>
                        </td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 500, fontSize: 14 }}>{m.label}</div>
                          {m.description && (
                            <div style={{ color: '#888', fontSize: 11, marginTop: 2 }}>{m.description}</div>
                          )}
                          {m.route && (
                            <div style={{ color: '#aaa', fontSize: 11, marginTop: 2, fontFamily: 'monospace' }}>{m.route}</div>
                          )}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                            <TypeBadge type={m.module_type} />
                            <JurisdictionBadge jurisdiction={m.jurisdiction} />
                            <PracticeAreaBadge practiceArea={m.practice_area} />
                          </div>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center', color: '#555' }}>
                          {m.active_client_count ?? 0}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <ActiveToggle m={m} busy={busy === m.id} onToggle={() => toggleActive(m)} />
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <button className="btn btn-sm"
                            onClick={() => navigate(`/admin/dashboard/modules/${m.id}`)}>
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Mobile card list */}
          <div className="mobile-only">
            {modules.map((m, i) => (
              <div key={m.id} className="card" style={{ padding: 14, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 14, fontWeight: 500 }}>{m.label}</span>
                      <TypeBadge type={m.module_type} />
                      <JurisdictionBadge jurisdiction={m.jurisdiction} />
                      <PracticeAreaBadge practiceArea={m.practice_area} />
                    </div>
                    {m.description && (
                      <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888', marginTop: 2 }}>{m.description}</div>
                    )}
                    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888', marginTop: 8 }}>
                      {m.active_client_count ?? 0} active client{m.active_client_count === 1 ? '' : 's'}
                    </div>
                  </div>
                  <ActiveToggle m={m} busy={busy === m.id} onToggle={() => toggleActive(m)} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, gap: 8 }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-sm" style={{ padding: '4px 10px' }}
                      disabled={i === 0} onClick={() => moveUp(i)} aria-label="Move up">↑</button>
                    <button className="btn btn-sm" style={{ padding: '4px 10px' }}
                      disabled={i === modules.length - 1} onClick={() => moveDown(i)} aria-label="Move down">↓</button>
                  </div>
                  <button className="btn btn-sm"
                    onClick={() => navigate(`/admin/dashboard/modules/${m.id}`)}>
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
